import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import msalInstance, { loginRequest } from "../utils/msalConfig";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";
import { getPostLoginPath } from "../config/featureFlags";

export default function OutlookCallback() {
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [status, setStatus] = useState("processing");
  const [error, setError] = useState(null);
  const processedRef = useRef(false);
  const processingStartedRef = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      // Prevent double processing - use a lock mechanism
      // But allow retry if previous attempt failed (status is error)
      if (processingStartedRef.current) {
        console.log(
          "⏭️ Callback processing already started, skipping duplicate call..."
        );
        return;
      }

      // Set lock immediately
      processingStartedRef.current = true;
      processedRef.current = true;
      setStatus("processing");

      console.log("🔒 Callback processing lock acquired");

      try {
        console.log("🔄 Processing Outlook callback...");
        console.log("   Current URL:", window.location.href);
        console.log("   URL Hash:", window.location.hash);
        console.log("   Pathname:", window.location.pathname);
        console.log("   Hash length:", window.location.hash.length);

        // Ensure MSAL is initialized
        if (!msalInstance.getConfiguration()) {
          console.log("⏳ MSAL not initialized yet, waiting...");
          await msalInstance.initialize();
          console.log("✅ MSAL initialized");
        }

        // Wait a bit for MSAL to be fully ready
        await new Promise((resolve) => setTimeout(resolve, 200));

        // Check if we have a hash in URL - MSAL needs this to process
        const urlHash = window.location.hash;
        if (!urlHash || !urlHash.includes("code=")) {
          console.warn("⚠️ No authorization code in URL hash");
          console.warn("   This might mean:");
          console.warn("   1. Microsoft didn't redirect here correctly");
          console.warn("   2. Hash was cleared before we could process it");
          console.warn("   3. We're on the wrong page");

          // Check sessionStorage for stored hash
          const storedHash = sessionStorage.getItem(
            "msal.ffe5632a-a6d6-41fe-ae1a-4adb769da6ac.urlHash"
          );
          if (storedHash) {
            console.log(
              "✅ Found hash in sessionStorage, MSAL should process it"
            );
            // MSAL should have processed this, let's check accounts
          }
        }

        // CRITICAL: handleRedirectPromise MUST be called immediately when page loads
        // MSAL processes the hash synchronously, so we can't wait for React to render
        console.log("🔍 Processing MSAL redirect immediately...");
        console.log("   URL:", window.location.href);
        console.log("   Hash present:", !!window.location.hash);
        console.log("   Hash length:", window.location.hash.length);

        let response = null;

        // First, ensure MSAL is initialized
        try {
          await msalInstance.initialize();
          console.log("✅ MSAL initialized");
        } catch (initError) {
          // MSAL might already be initialized, that's okay
          console.log(
            "ℹ️ MSAL initialization:",
            initError.message || "already initialized"
          );
        }

        // CRITICAL FIX: handleRedirectPromise() hangs - use a workaround
        // Check accounts first, then try handleRedirectPromise with strict timeout
        // If it hangs, manually parse hash and use acquireTokenByCode

        console.log("🔍 Checking if MSAL already processed redirect...");
        let accounts = msalInstance.getAllAccounts();
        console.log("   Accounts found:", accounts.length);

        if (accounts.length > 0) {
          console.log("✅ MSAL has account - redirect was already processed!");
          console.log("   Account ID:", accounts[0].homeAccountId);
          console.log("   Using silent token acquisition...");

          try {
            response = await msalInstance.acquireTokenSilent({
              ...loginRequest,
              account: accounts[0],
            });
            if (response && response.accessToken) {
              console.log("✅ Got token via silent acquisition!");
            }
          } catch (silentErr) {
            console.error("❌ Silent acquisition failed:", silentErr.message);
          }
        }

        // If no account found, try handleRedirectPromise with timeout
        if (!response) {
          console.log(
            "🔍 No account found, calling handleRedirectPromise with timeout..."
          );

          // Create a promise that will definitely resolve/reject
          const handleRedirectWithTimeout = () => {
            return new Promise(async (resolve, reject) => {
              // Set timeout first
              const timeoutId = setTimeout(() => {
                console.error("❌ handleRedirectPromise TIMEOUT after 3s");
                reject(new Error("TIMEOUT"));
              }, 3000);

              try {
                // Call handleRedirectPromise
                const result = await msalInstance.handleRedirectPromise();
                clearTimeout(timeoutId);
                resolve(result);
              } catch (err) {
                clearTimeout(timeoutId);
                reject(err);
              }
            });
          };

          try {
            response = await handleRedirectWithTimeout();
            console.log("✅ handleRedirectPromise returned:", !!response);
          } catch (error) {
            if (error.message === "TIMEOUT") {
              console.error(
                "❌ handleRedirectPromise timed out - promise is hanging"
              );
              console.error("   This is a known MSAL issue");

              // Check if account appeared during the timeout
              accounts = msalInstance.getAllAccounts();
              if (accounts.length > 0) {
                console.log("✅ Account appeared after timeout!");
                try {
                  response = await msalInstance.acquireTokenSilent({
                    ...loginRequest,
                    account: accounts[0],
                  });
                  if (response && response.accessToken) {
                    console.log("✅ Got token after timeout!");
                  }
                } catch (finalErr) {
                  console.error(
                    "❌ Final silent acquisition failed:",
                    finalErr.message
                  );
                  // Last resort - throw error to show user
                  throw new Error(
                    "Authentication failed. Please try logging in again."
                  );
                }
              } else {
                console.error(
                  "❌ No account found - redirect processing failed"
                );
                throw new Error(
                  "Authentication timeout. Please try logging in again."
                );
              }
            } else {
              console.error("❌ handleRedirectPromise error:", error.message);
              throw error;
            }
          }
        }

        console.log("📥 handleRedirectPromise result:", {
          hasResponse: !!response,
          hasAccessToken: !!response?.accessToken,
          hasIdToken: !!response?.idToken,
          accountId: response?.account?.homeAccountId,
          errorCode: response?.errorCode,
          errorMessage: response?.errorMessage,
          responseType: typeof response,
          responseKeys: response ? Object.keys(response) : [],
        });

        // If response has error, log it
        if (response && response.errorCode) {
          console.error("❌ MSAL response has error:", {
            errorCode: response.errorCode,
            errorMessage: response.errorMessage,
            error: response.error,
          });
        }

        if (!response) {
          console.warn("⚠️ No response from MSAL handleRedirectPromise");
          console.log("   This might mean:");
          console.log("   1. We're not coming from a Microsoft redirect");
          console.log("   2. MSAL already processed the redirect");
          console.log(
            "   3. The hash was cleared before MSAL could process it"
          );

          // Check if we have a hash in the URL that MSAL should process
          const urlHash = window.location.hash;
          if (urlHash && urlHash.includes("code=")) {
            console.log("⚠️ Found code in URL hash but MSAL didn't process it");
            console.log("   Hash preview:", urlHash.substring(0, 100) + "...");
            console.log("   Waiting a bit and retrying...");

            // Wait longer for MSAL to process - it might need more time
            await new Promise((resolve) => setTimeout(resolve, 1000));

            console.log("🔄 Retrying handleRedirectPromise...");
            const retryResponse = await msalInstance.handleRedirectPromise();

            if (retryResponse) {
              console.log("✅ Got response on retry!");
              response = retryResponse; // Use retry response
            } else {
              console.warn("⚠️ Still no response after retry");
              // Check if MSAL stored the account anyway
              const accounts = msalInstance.getAllAccounts();
              console.log("   Accounts found:", accounts.length);
              if (accounts.length > 0) {
                console.log("   Account:", accounts[0].homeAccountId);
              }
            }
          }

          // If still no response, check if MSAL already processed and stored account
          if (!response) {
            const accounts = msalInstance.getAllAccounts();
            if (accounts.length > 0 && urlHash && urlHash.includes("code=")) {
              console.log(
                "⚠️ MSAL has account but handleRedirectPromise returned null"
              );
              console.log(
                "   This might mean MSAL processed it but didn't return response"
              );
              console.log("   Trying to get token silently...");

              try {
                const silentResponse = await msalInstance.acquireTokenSilent({
                  ...loginRequest,
                  account: accounts[0],
                });

                if (silentResponse && silentResponse.accessToken) {
                  console.log("✅ Got token silently after redirect!");
                  response = silentResponse; // Use silent response
                }
              } catch (silentError) {
                console.error(
                  "❌ Silent token acquisition failed:",
                  silentError
                );
              }
            }
          }

          // Final check - if we still don't have response, try one more time
          if (!response && urlHash && urlHash.includes("code=")) {
            console.log("🔄 Final retry attempt...");
            await new Promise((resolve) => setTimeout(resolve, 500));
            const finalRetry = await msalInstance.handleRedirectPromise();
            if (finalRetry) {
              console.log("✅ Got response on final retry!");
              response = finalRetry;
            }
          }

          // If we got response from retry/silent, continue processing below
          if (!response) {
            // Final fallback - check accounts
            const accounts = msalInstance.getAllAccounts();
            if (accounts.length > 0) {
              console.log(
                "✅ Found existing account, trying to get token silently..."
              );
              try {
                const silentResponse = await msalInstance.acquireTokenSilent({
                  ...loginRequest,
                  account: accounts[0],
                });

                if (silentResponse && silentResponse.accessToken) {
                  console.log("✅ Got token silently, proceeding...");
                  response = silentResponse;
                }
              } catch (silentError) {
                console.error(
                  "❌ Silent token acquisition failed:",
                  silentError
                );
              }
            }

            // If still no response, show error
            if (!response) {
              console.error(
                "❌ Cannot proceed - no MSAL response and no accounts"
              );
              console.error("   URL:", window.location.href);
              console.error("   Hash:", window.location.hash);
              console.error("   Accounts:", accounts.length);

              setError(
                "No authentication response received. Please try logging in again."
              );
              setStatus("error");
              setTimeout(() => {
                // Clear any stuck state
                const interactionKeys = Object.keys(sessionStorage).filter(
                  (key) => key.includes("msal.interaction")
                );
                interactionKeys.forEach((key) =>
                  sessionStorage.removeItem(key)
                );
                navigate("/login", { replace: true });
              }, 3000);
              return;
            }
          }
        }

        console.log("✅ MSAL response received:", {
          accountId: response.account?.homeAccountId,
          hasAccessToken: !!response.accessToken,
          hasIdToken: !!response.idToken,
          account: response.account,
        });

        // Extract tokens
        const accessToken = response.accessToken;
        const idToken = response.idToken;

        if (!accessToken) {
          console.error("❌ No access token in response");
          console.error("   Response object:", response);
          setError("Failed to get access token from Microsoft");
          setStatus("error");
          setTimeout(() => {
            // Clear MSAL state
            const interactionKeys = Object.keys(sessionStorage).filter((key) =>
              key.includes("msal.interaction")
            );
            interactionKeys.forEach((key) => sessionStorage.removeItem(key));
            navigate("/login", { replace: true });
          }, 3000);
          return;
        }

        console.log("✅ Tokens extracted successfully:", {
          accessTokenLength: accessToken.length,
          idTokenLength: idToken?.length || 0,
        });

        // Send tokens to backend
        console.log("📤 Sending tokens to backend...");
        console.log("   API Base URL:", api.defaults.baseURL);
        console.log("   Endpoint:", "/auth/outlook/callback");
        console.log(
          "   Full URL:",
          `${api.defaults.baseURL}/auth/outlook/callback`
        );
        console.log("   Has accessToken:", !!accessToken);
        console.log("   Has idToken:", !!idToken);
        console.log("   AccessToken length:", accessToken?.length || 0);

        const backendResponse = await api.post("/auth/outlook/callback", {
          accessToken,
          idToken,
        });

        console.log("✅ Backend responded!");
        console.log("   Status:", backendResponse.status);
        console.log("   Status Text:", backendResponse.statusText);

        console.log("✅ Backend response:", backendResponse);
        console.log("✅ Backend response.data:", backendResponse.data);
        console.log(
          "✅ Backend response.data type:",
          typeof backendResponse.data
        );

        // Handle response - serverless-offline should parse Lambda response automatically
        let responseData = backendResponse.data;

        // If response is a string (shouldn't happen but handle it)
        if (typeof responseData === "string") {
          try {
            responseData = JSON.parse(responseData);
          } catch (e) {
            console.error("❌ Failed to parse response as JSON:", e);
            throw new Error("Invalid response format from server");
          }
        }

        // If response has a body field (Lambda format), extract it
        if (
          responseData &&
          typeof responseData === "object" &&
          responseData.body
        ) {
          console.log("⚠️ Response has body field, parsing...");
          if (typeof responseData.body === "string") {
            responseData = JSON.parse(responseData.body);
          } else {
            responseData = responseData.body;
          }
        }

        console.log("✅ Parsed response data:", responseData);

        const { token, user: userData, isNewUser } = responseData;

        if (!token || !userData) {
          console.error("❌ Invalid backend response:", responseData);
          setError("Invalid response from server");
          setStatus("error");
          setTimeout(() => navigate("/login"), 3000);
          return;
        }

        // Store token and user data FIRST
        console.log("💾 Storing authentication data...");
        localStorage.setItem("token", token);
        localStorage.setItem("user", JSON.stringify(userData));
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        console.log("✅ Data stored in localStorage");
        console.log("   Token stored:", !!localStorage.getItem("token"));
        console.log("   User stored:", !!localStorage.getItem("user"));

        // Update auth context - this should trigger re-render
        console.log("🔄 Updating AuthContext...");
        const updatedUser = updateUser({ ...userData, token });

        console.log("✅ AuthContext updated:", {
          user: updatedUser,
          email: updatedUser?.email,
          role: updatedUser?.role,
        });

        console.log("✅ Authentication successful:", {
          email: userData.email,
          isNewUser: !!isNewUser,
          userId: userData.id,
        });

        setStatus("success");

        // Wait a bit longer to ensure AuthContext processes the update
        // Then redirect to dashboard
        setTimeout(() => {
          const next = getPostLoginPath(userData);
          console.log("🚀 Redirecting after OAuth:", next);
          window.location.href = next;
        }, 1500);
      } catch (err) {
        console.error("❌ Callback error:", err);
        console.error("Error details:", {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status,
        });

        const errorMessage =
          err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Authentication failed";
        setError(errorMessage);
        setStatus("error");

        // Redirect to login after showing error
        setTimeout(() => {
          navigate("/login", { replace: true });
        }, 3000);
      }
    };

    handleCallback();
  }, [navigate, updateUser]);

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gray-50">
      <motion.div
        className="text-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {status === "processing" && (
          <>
            <div className="mb-6">
              <svg
                className="animate-spin h-16 w-16 text-primary mx-auto"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Authenticating with Outlook...
            </h2>
            <p className="text-gray-600">Please wait while we sign you in.</p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="mb-6">
              <svg
                className="h-16 w-16 text-green-500 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Authentication Successful!
            </h2>
            <p className="text-gray-600">Redirecting to dashboard...</p>
          </>
        )}

        {status === "error" && (
          <>
            <div className="mb-6">
              <svg
                className="h-16 w-16 text-red-500 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-red-600 mb-2">
              Authentication Failed
            </h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <p className="text-sm text-gray-500">
              Redirecting to login page...
            </p>
          </>
        )}
      </motion.div>
    </div>
  );
}
